import { useSelector } from 'react-redux';
import { selectUser, selectUserTransactions } from '../selectors/user';
import { selectBrandSettings } from '../selectors';
import {
  getDepositStatus,
  isDepositRequired,
} from '../helpers/onBoardingHelper';

type Options = {
  readOnlyMode?: boolean;
};

export const useAccountPaymentFlags = (options: Options = {}) => {
  const { readOnlyMode = false } = options;
  const user = useSelector(selectUser) as any;
  const brandSettings = useSelector(selectBrandSettings) as any;
  const transactions = useSelector(selectUserTransactions) as any;
  const openTransactions =
    transactions?.transactions?.filter((t: any) => t?.status === 'open') || [];

  const sepaPayment = user?.paymentMethods?.find((method) => method.sepa?.iban);
  const sepaIban = sepaPayment?.sepa?.iban;

  const isBlocked = !!user?.blocked;
  const hasOnlyBlockedPayment =
    (user?.paymentMethods?.length || 0) === 1 &&
    !!user?.paymentMethods?.find((p: any) => p?.status === 'blocked');

  const isSepaOnlyBrand =
    (brandSettings?.allowedPaymentTypes?.length || 0) === 1 &&
    brandSettings?.allowedPaymentTypes?.[0] === 'sepa';

  const depositRequired = isDepositRequired(user);
  const depositStatus = getDepositStatus(user);
  const supportedPaymentTypes = user?.supportedPaymentTypes || [];

  const hasDepositBalance = !!(
    user?.deposit?.balance && user?.deposit?.balance > 0
  );
  const hasSepaPayment = !!sepaPayment;

  const isLowSchufaCredit =
    isSepaOnlyBrand &&
    user?.creditworthinessStatus === 'failed' &&
    !isBlocked &&
    !!user?.sepaFirstAddedAt &&
    !supportedPaymentTypes?.includes('sepa') &&
    !hasDepositBalance &&
    !depositRequired &&
    !user?.deposit?.receiptNo;

  const showSepaPendingCWWarning =
    hasSepaPayment &&
    sepaPayment?.status === 'pending' &&
    !user?.creditworthinessApproved &&
    !isLowSchufaCredit;

  const hideAddPayments =
    !!readOnlyMode ||
    !!isLowSchufaCredit ||
    supportedPaymentTypes.length === 0 ||
    (!isBlocked && isSepaOnlyBrand && hasOnlyBlockedPayment);

  const showDeposit =
    depositRequired &&
    // depositStatus === 'open' &&
    !!user?.deposit?.toPayForSepaUnlock &&
    !isBlocked &&
    !!user?.paymentMethods?.[0]?.sepa?.iban;

  const showFallbackPaymentDialog =
    !readOnlyMode &&
    !isLowSchufaCredit &&
    supportedPaymentTypes.length === 0 &&
    !depositRequired &&
    !user?.deposit?.balance;

  const showBlockedPaymentWarning =
    isSepaOnlyBrand &&
    hasOnlyBlockedPayment &&
    openTransactions.length > 0 &&
    !isLowSchufaCredit;

  const shouldHideSepaInList =
    (depositRequired && depositStatus === 'open' && !user?.deposit?.balance) ||
    showSepaPendingCWWarning ||
    isLowSchufaCredit ||
    user?.creditworthinessStatus === 'failed';

  const userPayments = (user?.paymentMethods || [])?.filter((payment: any) => {
    if (shouldHideSepaInList && payment?.sepa) return false;
    // TODO: Remove pending status handling for Apple Pay once backend returns 'verified' status correctly
    // Allow pending Apple Pay payments to be shown
    const isApplePay =
      payment?.methodType === 'applePay' || payment?.methodType === 'applepay';
    if (isApplePay && payment?.status === 'pending') return true;
    return !['pending', 'blocked']?.includes(payment?.status);
  });

  return {
    sepaPayment,
    sepaIban,
    isLowSchufaCredit,
    hideAddPayments,
    showDeposit,
    showSepaPendingCWWarning,
    userPayments,
    showFallbackPaymentDialog,
    showBlockedPaymentWarning,
  } as const;
};
